/**
 * Issue link helpers — grouping and relation options, mirroring Jira's
 * "Linked work items" presentation.
 */

import type { JiraIssueLink, JiraIssueLinkType } from '@shared/types.js';

export interface LinkRelationOption {
  /** Stable select value: `${typeName}|${direction}` */
  value: string;
  /** Relation text read from the current issue, e.g. "is blocked by" */
  label: string;
  typeName: string;
  direction: 'inward' | 'outward';
}

export interface LinkGroup {
  relation: string;
  links: JiraIssueLink[];
}

/** Jira lists blocking relations first, then the rest in link-type order. */
const RELATION_ORDER = ['is blocked by', 'blocks'];

export function isBlockedByLink(link: JiraIssueLink): boolean {
  return link.typeName === 'Blocks' && link.direction === 'inward';
}

/** Links whose blocker is not yet done. */
export function getOpenBlockers(links: JiraIssueLink[] | undefined): JiraIssueLink[] {
  return (links ?? []).filter((l) => isBlockedByLink(l) && l.issue.status.category !== 'done');
}

export function groupLinksByRelation(links: JiraIssueLink[]): LinkGroup[] {
  const groups = new Map<string, JiraIssueLink[]>();
  for (const link of links) {
    const list = groups.get(link.type);
    if (list) list.push(link);
    else groups.set(link.type, [link]);
  }
  const rank = (relation: string): number => {
    const idx = RELATION_ORDER.indexOf(relation);
    return idx === -1 ? RELATION_ORDER.length : idx;
  };
  return [...groups.entries()]
    .map(([relation, list]) => ({ relation, links: list }))
    .sort((a, b) => rank(a.relation) - rank(b.relation));
}

/**
 * Build the relation dropdown the way Jira does: each link type contributes
 * its outward and inward phrasing (once, if both read the same).
 */
export function buildRelationOptions(types: JiraIssueLinkType[]): LinkRelationOption[] {
  const options: LinkRelationOption[] = [];
  const seen = new Set<string>();
  for (const t of types) {
    for (const direction of ['outward', 'inward'] as const) {
      const label = direction === 'outward' ? t.outward : t.inward;
      if (seen.has(label)) continue;
      seen.add(label);
      options.push({ value: `${t.name}|${direction}`, label, typeName: t.name, direction });
    }
  }
  return options;
}

export function describeLinks(links: JiraIssueLink[]): string {
  return links.map((l) => `${l.type} ${l.issue.key} (${l.issue.status.name})`).join('\n');
}
